import React, { useState } from "react";
import { getTranslation } from "../../utils/translations";
import { APP_PATHS } from "../../config/constants";
import ResponsiveImage from "../ResponsiveImage";
import HeartIcon from "../Icons/HeartIcon";
import IframeModal from "../Common/IframeModal";
import PhotoGalleryModal from "../Common/PhotoGalleryModal";
import "./ParcelPopupContent.css";
import Lottie from "lottie-react";

// Import Lottie animations
import liveAnimation from "../../assets/animations/live.json";
import tour360Animation from "../../assets/animations/360-view-white.json";
import photoGalleryAnimation from "../../assets/animations/09-Photo gallery_opt.json";

const ParcelPopupContent = ({
  parcelData,
  projectData,
  language,
  isLoading = false,
  hasError = false,
  onVirtualTour,
  onShare,
  onReserve,
  onFavorite,
  isFavorited = false,
  onToggleFavorite,
  isLoadingFavorite = false
}) => {
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeTitle, setIframeTitle] = useState('');
  const [blueprint3DAnimation, setBlueprint3DAnimation] = useState(null);
  const [blueprint2DAnimation, setBlueprint2DAnimation] = useState(null);
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);

  // Load blueprint animations
  React.useEffect(() => {
    const loadAnimations = async () => {
      try {
        const [blueprint3D, blueprint2D] = await Promise.all([
          fetch('/lottiefiles/8-3D Modeling_opt.json').then(res => res.json()),
          fetch('/lottiefiles/17 Blueprint_opt.json').then(res => res.json())
        ]);
        setBlueprint3DAnimation(blueprint3D);
        setBlueprint2DAnimation(blueprint2D);
      } catch (error) {
      }
    };
    loadAnimations();
  }, []);
  const formatPrice = (price) => {
    if (!price) return "N/A";
    // return new Intl.NumberFormat(language === "ar" ? "ar-SA" : "en-US").format(price);
    return price;
  };

  const formatArea = (area) => {
    if (!area) return "N/A";
    // return new Intl.NumberFormat(language === "ar" ? "ar-SA" : "en-US").format(area);
    return area;
  };

  const formatROI = (roi) => {
    if (!roi) return "N/A";
    // Add % symbol if not already present
    const roiStr = roi.toString();
    return roiStr.includes('%') ? roiStr : `${roiStr}%`;
  };


  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "#22c55e";
      case "not available":
        return "#3b82f6";
      case "reserved":
        return "#f59e0b";
      case "sold":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return getTranslation("available", language);
      case "not available":
        return getTranslation("notAvailable", language);
      case "reserved":
        return getTranslation("reserved", language);
      case "sold":
        return getTranslation("sold", language);
      default:
        return status || "N/A";
    }
  };

  const handleVirtualTourClick = () => {
    // Don't execute if button is disabled due to loading or no URL
    if (isLoading || !projectData?.virtual_tour_url) {
      return;
    }

    // Use project's virtual tour URL from API data
    const virtualTourUrl = projectData.virtual_tour_url;

    // Open in iframe modal instead of new tab
    setIframeUrl(virtualTourUrl);
    setIframeTitle(language === "ar" ? "جولة افتراضية 360°" : "360° Virtual Tour");
    setIframeModalOpen(true);
  };

  const handleLiveCameraClick = () => {
    // Don't execute if button is disabled due to loading or no URL
    if (isLoading || !projectData?.live_camera_url) {
      return;
    }

    // Use project's live camera URL from API data
    const liveCameraUrl = projectData.live_camera_url;

    // Open in iframe modal instead of new tab
    setIframeUrl(liveCameraUrl);
    setIframeTitle(language === "ar" ? "البث المباشر" : "Live Stream");
    setIframeModalOpen(true);
  };

  const handle3DBlueprintClick = () => {
    // Don't execute if button is disabled due to loading or no URL
    if (isLoading || !parcelData?.blueprint_3d_url) {
      return;
    }

    // Use parcel's 3D blueprint URL from API data
    const blueprint3DUrl = parcelData.blueprint_3d_url;

    // Open in iframe modal instead of new tab
    setIframeUrl(blueprint3DUrl);
    setIframeTitle(language === "ar" ? "مخطط ثلاثي الأبعاد" : "3D Blueprint");
    setIframeModalOpen(true);
  };

  const handle2DBlueprintClick = () => {
    // Check if we have either URL or image
    const hasUrl = parcelData?.blueprint_2d_url;
    const hasImage = parcelData?.blueprint_2d_image_url;

    if (isLoading || (!hasUrl && !hasImage)) {
      return;
    }

    const title = language === "ar" ? "مخطط ثنائي الأبعاد" : "2D Blueprint";

    // Prefer image over URL for better viewing experience
    if (hasImage) {
      // Use photo gallery modal for single image
      const projectName = language === "ar"
        ? (parcelData?.block?.project?.name_ar || parcelData?.project?.name || "المشروع")
        : (parcelData?.block?.project?.name_en || parcelData?.project?.name || "Project");

      openPhotoGalleryModal([parcelData.blueprint_2d_image_url], title, language);
    } else if (hasUrl) {
      // Use iframe modal for URL
      setIframeUrl(parcelData.blueprint_2d_url);
      setIframeTitle(title);
      setIframeModalOpen(true);
    }
  };

  const handleCloseIframeModal = () => {
    setIframeModalOpen(false);
    setIframeUrl('');
    setIframeTitle('');
  };

  const handlePhotoGalleryClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    if (parcelData.unit_photo_gallery_image_urls && parcelData.unit_photo_gallery_image_urls.length > 0) {
      setPhotoGalleryOpen(true);
    } else {
      alert(language === "ar" ? "لا توجد صور متاحة لهذه الوحدة" : "No photo gallery available for this unit");
    }
  };

  const handleClosePhotoGallery = () => {
    setPhotoGalleryOpen(false);
  };

  const handleContractDocumentClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    if (parcelData.contract_document_url || parcelData.contract_document) {
      // Open the contract document in a new tab
      const documentUrl = parcelData.contract_document_url || parcelData.contract_document;
      window.open(documentUrl, '_blank');
    } else {
      alert(language === "ar" ? "لا يوجد مستند عقد متاح لهذه الوحدة" : "No contract document available for this unit");
    }
  };

  const handleShareClick = () => {

    // Create shared URL with project and unit parameters
    const baseUrl = window.location.origin + window.location.pathname;
    const projectId = projectData?.id || parcelData?.project?.id;
    const unitId = parcelData?.id;

    if (projectId && unitId) {
      const shareUrl = `${baseUrl}?project=${projectId}&shared=true&unit_id=${unitId}`;

      if (navigator.share) {
        // Use Web Share API if available
        navigator.share({
          title: `${getTranslation("parcel", language)} ${parcelData.parcel_number}`,
          text: "",
          url: shareUrl,
        }).catch((error) => {
          fallbackShare(shareUrl);
        });
      } else {
        fallbackShare(shareUrl);
      }
    } else {
      console.error("Missing project ID or unit ID for sharing");
      // Fallback to old behavior
      if (onShare) onShare(parcelData);
    }
  };

  const fallbackShare = (url) => {
    // Copy link to clipboard
    navigator.clipboard.writeText(url).then(() => {
      alert(language === "ar" ? "تم نسخ الرابط إلى الحافظة" : "Link copied to clipboard");
    }).catch(() => {
      // If copying fails, open WhatsApp share
      window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, '_blank');
    });
  };

  const handleReserveClick = () => {
    if (onReserve) onReserve(parcelData);
  };

  const handleFavoriteClick = () => {
    if (onToggleFavorite) {
      onToggleFavorite(parcelData);
    } else if (onFavorite) {
      onFavorite(parcelData);
    }
  };

  // Get the image source with fallback logic
  const getImageSource = () => {
    // Check if parcel has images
    if (parcelData.images && parcelData.images.length > 0) {
      const firstImage = parcelData.images[0];
      return typeof firstImage === 'string' ? firstImage : firstImage?.url;
    }

    // Check if default_unit_image_url is available in parcel data properties
    if (parcelData.default_unit_image_url) {
      return parcelData.default_unit_image_url;
    }

    // Fallback to project default image from project data
    if (projectData?.default_unit_image_url) {
      return projectData.default_unit_image_url;
    }

    // Final fallback to logo
    return APP_PATHS.PUBLIC_ASSETS.LOGO_ONLY;
  };

  return (

    <div className={`parcel-popup-content ${language === "ar" ? "rtl" : "ltr"}`} dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Loading Indicator */}
      {isLoading && (
        <div className="loading-indicator" style={{
          position: 'absolute',
          top: '10px',
          right: language === "ar" ? 'auto' : '10px',
          left: language === "ar" ? '10px' : 'auto',
          background: 'rgba(0, 123, 191, 0.1)',
          color: '#007cbf',
          padding: '4px 8px',
          borderRadius: '25px',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          zIndex: 10
        }}>
          <div style={{
            width: '12px',
            height: '12px',
            border: '2px solid #007cbf',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          {language === "ar" ? "جار التحميل..." : "Loading..."}
        </div>
      )}

      {/* Error Indicator */}
      {/* {hasError && !isLoading && (
        <div className="error-indicator" style={{
          position: 'absolute',
          top: '10px',
          right: language === "ar" ? 'auto' : '10px',
          left: language === "ar" ? '10px' : 'auto',
          background: 'rgba(239, 68, 68, 0.1)',
          color: '#ef4444',
          padding: '4px 8px',
          borderRadius: '25px',
          fontSize: '12px',
          zIndex: 10
        }}>
          {language === "ar" ? "بيانات أساسية" : "Basic data"}
        </div>
      )} */}

      {/* Mobile Layout - Image and buttons on top */}
      <div className="mobile-layout">
        <div className="mobile-image-container">
          <ResponsiveImage
            desktopImage={parcelData.unit_image_desktop_url}
            mobileImage={parcelData.unit_image_mobile_url}
            fallbackImage={getImageSource()}
            alt={`${getTranslation("parcel", language)} ${parcelData.parcel_number}`}
            className="mobile-unit-image"
            layout="responsive"
            aspectRatio="21:9"
            language={language}
          />
          <div className="mobile-action-buttons">
            <button
              className="favorite-btn"
              onClick={handleFavoriteClick}
              title={isFavorited ?
                (getTranslation("removeFromFavorites", language) || "Remove from Favorites") :
                (getTranslation("addToFavorites", language) || "Add to Favorites")
              }
              disabled={isLoading || isLoadingFavorite}
            >
              {isLoadingFavorite ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" opacity="0.3"/>
                  <path d="M12 2 A10 10 0 0 1 22 12" stroke="white" strokeWidth="2" fill="none">
                    <animateTransform
                      attributeName="transform"
                      attributeType="XML"
                      type="rotate"
                      from="0 12 12"
                      to="360 12 12"
                      dur="1s"
                      repeatCount="indefinite"
                    />
                  </path>
                </svg>
              ) : (
                <HeartIcon
                  filled={isFavorited}
                  size={20}
                  color={isFavorited ? "#dc2626" : "white"}
                />
              )}
            </button>
  {projectData?.virtual_tour_url && (
          <button
              className="share-icon"
              onClick={handleVirtualTourClick}
              title={projectData?.virtual_tour_url ?
                (language === "ar" ? "جولة افتراضية 360°" : "360° Virtual Tour") :
                (language === "ar" ? "لا توجد جولة افتراضية متاحة" : "No virtual tour available")
              }
              disabled={isLoading || !projectData?.virtual_tour_url}
            >
              <Lottie
                animationData={tour360Animation}
                loop={true}
                autoplay={true}
                style={{ width: 30, height: 30,  }}
              />
            </button>
        )}

            {parcelData?.blueprint_3d_url && blueprint3DAnimation && (
              <button
                className="share-icon"
                onClick={handle3DBlueprintClick}
                title={parcelData?.blueprint_3d_url ?
                  (language === "ar" ? "مخطط ثلاثي الأبعاد" : "3D Blueprint") :
                  (language === "ar" ? "لا يوجد مخطط ثلاثي الأبعاد متاح" : "No 3D blueprint available")
                }
                disabled={isLoading || !parcelData?.blueprint_3d_url}
              >
                <Lottie
                  animationData={blueprint3DAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: 30, height: 30 }}
                />
              </button>
            )}

            {parcelData?.blueprint_2d_url && blueprint2DAnimation && (
              <button
               className="share-icon"
                onClick={handle2DBlueprintClick}
                title={parcelData?.blueprint_2d_url ?
                  (language === "ar" ? "مخطط ثنائي الأبعاد" : "2D Blueprint") :
                  (language === "ar" ? "لا يوجد مخطط ثنائي الأبعاد متاح" : "No 2D blueprint available")
                }
                disabled={isLoading || !parcelData?.blueprint_2d_url}
              >
                <Lottie
                  animationData={blueprint2DAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: 30, height: 30 }}
                />
              </button>
            )}

            {parcelData?.unit_photo_gallery_image_urls && parcelData.unit_photo_gallery_image_urls.length > 0 && (
              <button
                className="share-icon"
                onClick={handlePhotoGalleryClick}
                title={parcelData.unit_photo_gallery_image_urls && parcelData.unit_photo_gallery_image_urls.length > 0 ?
                  (language === "ar" ? "معرض الصور" : "Photo Gallery") :
                  (language === "ar" ? "لا توجد صور متاحة" : "No photos available")
                }
                disabled={isLoading || !parcelData.unit_photo_gallery_image_urls || parcelData.unit_photo_gallery_image_urls.length === 0}
              >
                <Lottie
                  animationData={photoGalleryAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: 30, height: 30 }}
                />
              </button>
            )}

            {(parcelData?.contract_document_url || parcelData?.contract_document) && (
              <button
                className="share-icon"
                onClick={handleContractDocumentClick}
                title={(parcelData.contract_document_url || parcelData.contract_document) ?
                  (language === "ar" ? "مستند العقد" : "Contract Document") :
                  (language === "ar" ? "لا يوجد مستند عقد متاح" : "No contract document available")
                }
                disabled={isLoading || (!parcelData.contract_document_url && !parcelData.contract_document)}
              >
                <img src="/icons/contract-white.png" alt="Contract" style={{ width: 30, height: 30 }} />
              </button>
            )}

            <button
              className="share-btn"
              onClick={handleShareClick}
              title={getTranslation("share", language) || "Share"}
              disabled={isLoading}
            >
              <img src="/share white v2.png" alt="Share" />
            </button>
          </div>
        </div>

        <div className="mobile-content">
          <div className="mobile-title">
            <h3>
              {getTranslation("parcel", language)} {parcelData.parcel_number}
            </h3>
            <div
              className="status-badge"
              style={{ backgroundColor: getStatusColor(parcelData.sales_status) }}
            >
              {getStatusText(parcelData.sales_status)}
            </div>
          </div>

          <div className="mobile-details">
            <div className="detail-row">
                <span className="detail-value" dir="ltr">{parcelData.parcel_number}</span>

              <span className="detail-label">{getTranslation("unitNumber", language)}</span>
            </div>

            <div className="detail-row">
             <span className="detail-value" dir="ltr">
                {formatArea(parcelData.area)} {getTranslation("sqm", language)}
              </span>
              <span className="detail-label">{getTranslation("areaLabel", language)}</span>
             
            </div>

            <div className="detail-row">
             <span className="detail-value price-value" dir="ltr">
                {formatPrice(parcelData.total_price || parcelData.price_with_tax || 200000)} {getTranslation("sar", language)}
              </span>
              <span className="detail-label">{getTranslation("priceLabel", language)}</span>

            </div>

            {/* ROI Row */}
            {parcelData.expected_roi && (
              <div className="detail-row">
                <span className="detail-value roi-value" dir="ltr">
                  {formatROI(parcelData.expected_roi)}
                </span>
                <span className="detail-label">{getTranslation("expectedRoi", language)}</span>
              </div>
            )}

            {/* Reserve Button */}
            {parcelData.sales_status?.toLowerCase() === "available" && (
              <button
                className="reserve-btn"
                onClick={handleReserveClick}
                disabled={isLoading}
                style={{
                  opacity: isLoading ? 0.6 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  marginTop: '10px',
                  width: '100%'
                }}
              >
                {isLoading ?
                  (language === "ar" ? "جار التحميل..." : "Loading...") :
                  getTranslation("reserveUnit", language)
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Table Layout */}
      <table className="parcel-details-table desktop-layout">
        <tbody>
          {/* Header Row */}
          <tr className="header-row">
            <td className="image-cell" rowSpan="2">
              <div className="image-container">
                <ResponsiveImage
                  desktopImage={parcelData.unit_image_desktop_url}
                  mobileImage={parcelData.unit_image_mobile_url}
                  fallbackImage={getImageSource()}
                  alt={`${getTranslation("parcel", language)} ${parcelData.parcel_number}`}
                  className="popup-unit-image"
                  layout="responsive"
                  aspectRatio="1:1"
                  language={language}
                />
                <div className="action-buttons">
                  <button
                    className="favorite-btn"
                    onClick={handleFavoriteClick}
                    title={isFavorited ?
                      (getTranslation("removeFromFavorites", language) || "Remove from Favorites") :
                      (getTranslation("addToFavorites", language) || "Add to Favorites")
                    }
                    disabled={isLoading || isLoadingFavorite}
                  >
                    {isLoadingFavorite ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" opacity="0.3"/>
                        <path d="M12 2 A10 10 0 0 1 22 12" stroke="white" strokeWidth="2" fill="none">
                          <animateTransform
                            attributeName="transform"
                            attributeType="XML"
                            type="rotate"
                            from="0 12 12"
                            to="360 12 12"
                            dur="1s"
                            repeatCount="indefinite"
                          />
                        </path>
                      </svg>
                    ) : (
                      <HeartIcon
                        filled={isFavorited}
                        size={20}
                        color={isFavorited ? "#dc2626" : "white"}
                      />
                    )}
                  </button>

  {projectData?.virtual_tour_url && (
          <button
                             className="share-icon"

                    
                    onClick={handleVirtualTourClick}
                    title={projectData?.virtual_tour_url ?
                      (language === "ar" ? "جولة افتراضية 360°" : "360° Virtual Tour") :
                      (language === "ar" ? "لا توجد جولة افتراضية متاحة" : "No virtual tour available")
                    }
                    disabled={isLoading || !projectData?.virtual_tour_url}
                  >
                    <Lottie
                      animationData={tour360Animation}
                      loop={true}
                      autoplay={true}
                      style={{ width: 30, height: 30,  }}
                    />
                  </button>
        )}

                  {parcelData?.blueprint_3d_url && blueprint3DAnimation && (
                    <button
                      className="share-icon"
                      onClick={handle3DBlueprintClick}
                      title={parcelData?.blueprint_3d_url ?
                        (language === "ar" ? "مخطط ثلاثي الأبعاد" : "3D Blueprint") :
                        (language === "ar" ? "لا يوجد مخطط ثلاثي الأبعاد متاح" : "No 3D blueprint available")
                      }
                      disabled={isLoading || !parcelData?.blueprint_3d_url}
                    >
                      <Lottie
                        animationData={blueprint3DAnimation}
                        loop={true}
                        autoplay={true}
                        style={{ width: 30, height: 30 }}
                      />
                    </button>
                  )}

                  {(parcelData?.blueprint_2d_url || parcelData?.blueprint_2d_image_url) && blueprint2DAnimation && (
                    <button
                      className="share-icon"
                      onClick={handle2DBlueprintClick}
                      title={(parcelData?.blueprint_2d_url || parcelData?.blueprint_2d_image_url) ?
                        (language === "ar" ? "مخطط ثنائي الأبعاد" : "2D Blueprint") :
                        (language === "ar" ? "لا يوجد مخطط ثنائي الأبعاد متاح" : "No 2D blueprint available")
                      }
                      disabled={isLoading || (!parcelData?.blueprint_2d_url && !parcelData?.blueprint_2d_image_url)}
                    >
                      <Lottie
                        animationData={blueprint2DAnimation}
                        loop={true}
                        autoplay={true}
                        style={{ width: 30, height: 30 }}
                      />
                    </button>
                  )}

                  {parcelData?.unit_photo_gallery_image_urls && parcelData.unit_photo_gallery_image_urls.length > 0 && (
                    <button
                      className="share-icon"
                      onClick={handlePhotoGalleryClick}
                      title={parcelData.unit_photo_gallery_image_urls && parcelData.unit_photo_gallery_image_urls.length > 0 ?
                        (language === "ar" ? "معرض الصور" : "Photo Gallery") :
                        (language === "ar" ? "لا توجد صور متاحة" : "No photos available")
                      }
                      disabled={isLoading || !parcelData.unit_photo_gallery_image_urls || parcelData.unit_photo_gallery_image_urls.length === 0}
                    >
                      <Lottie
                        animationData={photoGalleryAnimation}
                        loop={true}
                        autoplay={true}
                        style={{ width: 30, height: 30 }}
                      />
                    </button>
                  )}

                  {(parcelData?.contract_document_url || parcelData?.contract_document) && (
                    <button
                      className="share-icon"
                      onClick={handleContractDocumentClick}
                      title={(parcelData.contract_document_url || parcelData.contract_document) ?
                        (language === "ar" ? "مستند العقد" : "Contract Document") :
                        (language === "ar" ? "لا يوجد مستند عقد متاح" : "No contract document available")
                      }
                      disabled={isLoading || (!parcelData.contract_document_url && !parcelData.contract_document)}
                    >
                      <img src="/icons/contract-white.png" alt="Contract" style={{ width: 30, height: 30 }} />
                    </button>
                  )}

                  <button
                    className="share-btn"
                    onClick={handleShareClick}
                    title={getTranslation("share", language) || "Share"}
                    disabled={isLoading}
                  >
                    <img src="/share white v2.png" alt="Share" width="30"/>
                  </button>
                </div>
              </div>
            </td>
            <td >
              <div className="title-cell">
                <h3>
                  {getTranslation("parcel", language)} {parcelData.parcel_number}
                </h3>
                <div
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(parcelData.sales_status) }}
                >
                  {getStatusText(parcelData.sales_status)}
                </div>
              </div>
            </td>
            {/* <td className="buttons-cell">

            </td> */}
          </tr>

          {/* Details Row */}
          <tr className="details-row">
            <td className="details-cell" colSpan="2">
              <div className="popup-details">
                {/* <div className="detail-row">
                                  <span className="detail-value" dir="ltr">{parcelData.parcel_number}</span>

                  <span className="detail-label">{getTranslation("unitNumber", language)}</span>
                </div> */}

                <div className="detail-row">
                <span className="detail-value" dir="ltr">
                    {formatArea(parcelData.area)} {getTranslation("sqm", language)}
                  </span>
                  <span className="detail-label">{getTranslation("areaLabel", language)}</span>
                  
                </div>

                <div className="detail-row">
                <span className="detail-value price-value" dir="ltr">
                    {formatPrice(parcelData.total_price || parcelData.price_with_tax || 200000)} {getTranslation("sar", language)}
                  </span>
                  <span className="detail-label">{getTranslation("priceLabel", language)}</span>

                </div>

                {/* ROI Row */}
                {parcelData.expected_roi && (
                  <div className="detail-row">
                    <span className="detail-value roi-value" dir="ltr">
                      {formatROI(parcelData.expected_roi)}
                    </span>
                    <span className="detail-label">{getTranslation("expectedRoi", language)}</span>
                  </div>
                )}

                {/* Reserve Button */}
                {parcelData.sales_status?.toLowerCase() === "available" && (
                  <button
                    className="reserve-btn"
                    onClick={handleReserveClick}
                    disabled={isLoading}
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      marginTop: '10px',
                      width: '100%'
                    }}
                  >
                    {isLoading ?
                      (language === "ar" ? "جار التحميل..." : "Loading...") :
                      getTranslation("reserveUnit", language)
                    }
                  </button>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* CSS for loading animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Iframe Modal */}
      <IframeModal
        isOpen={iframeModalOpen}
        onClose={handleCloseIframeModal}
        url={iframeUrl}
        title={iframeTitle}
        language={language}
      />

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        isOpen={photoGalleryOpen}
        onClose={handleClosePhotoGallery}
        images={parcelData?.unit_photo_gallery_image_urls || []}
        projectName={`${getTranslation("parcel", language)} ${parcelData?.parcel_number}`}
        language={language}
      />
    </div>
  );
};

export default ParcelPopupContent;
